import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

class EmailService {
  
  /**
   * Send an invoice/statement email with PDF attachment
   * This is the new pattern: cover letter HTML body + PDF attachment
   */
  static async sendInvoiceEmailWithAttachment(franchiseeId, student, invoiceData, coverLetterHTML, pdfBase64, pdfFilename) {
    try {
      console.log('📧 Starting email send with attachment...');
      
      // Get franchisee config
      const franchiseeRef = doc(db, 'Franchisees', franchiseeId);
      const franchiseeDoc = await getDoc(franchiseeRef);
      
      if (!franchiseeDoc.exists()) {
        throw new Error('Franchisee data not found');
      }
      
      const franchiseeData = franchiseeDoc.data();
      
      if (!franchiseeData.useEmailService) {
        throw new Error('Email service is not enabled. Please configure email in Setup → Email Configuration.');
      }
      
      if (!franchiseeData.outgoingServer || !franchiseeData.port || 
          !franchiseeData.username || !franchiseeData.password) {
        throw new Error('Email configuration is incomplete. Please check Setup → Email Configuration.');
      }
      
      // Get email from primaryContact object
      const recipientEmail = student.primaryContact?.email || 
                            student.parentEmail || 
                            student.guardianEmail;
      
      if (!recipientEmail) {
        throw new Error(`No email address found for ${student.studentName}`);
      }
      
      // Determine doc type based on balance
      const balance = invoiceData.totalBalance || 0;
      const docType = balance > 0 ? 'Invoice' : 'Statement';
      const today = new Date().toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
      const titleSuffix = franchiseeData.statementTitle || '';
      const titleBase = titleSuffix ? `Beaux-Arts ${titleSuffix}` : 'Beaux-Arts';
      const subject = `${titleBase} ${docType} - ${student.studentName} - ${today}`;
      
      // Call Cloud Function
      const response = await fetch(
        'https://us-central1-babs-2ee3f.cloudfunctions.net/sendBABSInvoiceEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            franchiseeId: franchiseeId,
            to: recipientEmail,
            subject: subject,
            html: coverLetterHTML,
            pdfBase64: pdfBase64,
            pdfFilename: pdfFilename,
            studentName: student.studentName || '',
            schoolName: student.schoolName || '',
            smtpConfig: {
              host: franchiseeData.outgoingServer,
              port: franchiseeData.port,
              secure: franchiseeData.useSSL || false,
              auth: {
                user: franchiseeData.username,
                pass: franchiseeData.password
              }
            }
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }
      
      const result = await response.json();
      
      return {
        success: true,
        message: `Email sent to ${recipientEmail}`,
        recipientEmail: recipientEmail,
        messageId: result.messageId
      };
      
    } catch (error) {
      console.error('❌ Error sending email:', error);
      
      return {
        success: false,
        message: error.message || 'Failed to send invoice email',
        recipientEmail: student.primaryContact?.email || student.parentEmail || 'unknown'
      };
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * Now redirects to the new attachment method if PDF data is provided
   */
  static async sendInvoiceEmail(franchiseeId, student, invoiceData, invoiceHTML) {
    // Call the old endpoint without attachment for backward compat
    try {
      const franchiseeRef = doc(db, 'Franchisees', franchiseeId);
      const franchiseeDoc = await getDoc(franchiseeRef);
      
      if (!franchiseeDoc.exists()) {
        throw new Error('Franchisee data not found');
      }
      
      const franchiseeData = franchiseeDoc.data();
      
      if (!franchiseeData.useEmailService) {
        throw new Error('Email service is not enabled');
      }
      
      if (!franchiseeData.outgoingServer || !franchiseeData.port || 
          !franchiseeData.username || !franchiseeData.password) {
        throw new Error('Email configuration is incomplete');
      }
      
      const recipientEmail = student.primaryContact?.email || 
                            student.parentEmail || 
                            student.guardianEmail;
      
      if (!recipientEmail) {
        throw new Error('No email address found for this student');
      }
      
      const subject = `Beaux-Arts Invoice - ${student.studentName} - ${new Date().toLocaleDateString()}`;
      
      const response = await fetch(
        'https://us-central1-babs-2ee3f.cloudfunctions.net/sendBABSInvoiceEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            franchiseeId: franchiseeId,
            to: recipientEmail,
            subject: subject,
            html: invoiceHTML,
            smtpConfig: {
              host: franchiseeData.outgoingServer,
              port: franchiseeData.port,
              secure: franchiseeData.useSSL || false,
              auth: {
                user: franchiseeData.username,
                pass: franchiseeData.password
              }
            }
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }
      
      return {
        success: true,
        message: 'Invoice email sent successfully!'
      };
      
    } catch (error) {
      console.error('❌ Error sending email:', error);
      return {
        success: false,
        message: error.message || 'Failed to send invoice email'
      };
    }
  }

  /**
   * Generate a COVER LETTER HTML email
   * This is the friendly letter that goes in the email body.
   * The detailed fee breakdown is in the PDF attachment.
   */
  static generateCoverLetterHTML(student, franchiseeData, schoolData, invoiceData, customMessage) {
    const today = new Date().toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
    const totalBalance = invoiceData.totalBalance || 0;
    const titleSuffix = franchiseeData.statementTitle || '';
    const titleBase = titleSuffix ? `BEAUX-ARTS ${titleSuffix}` : 'BEAUX-ARTS';
    const docType = totalBalance > 0 ? 'INVOICE' : 'STATEMENT';
    const docTypeLower = totalBalance > 0 ? 'invoice' : 'statement';
    const subtitle = franchiseeData.statementSubtitle || '';
    
    // Build franchisee details for header
    const detailParts = [];
    if (franchiseeData.showFranchiseeNameOnStatement && franchiseeData.name) {
      detailParts.push(franchiseeData.name);
    }
    const contactNumber = franchiseeData.whatsapp || franchiseeData.phone || '';
    if (franchiseeData.showFranchiseePhoneOnStatement !== false && contactNumber) {
      detailParts.push(`Contact: ${contactNumber}`);
    }
    if (franchiseeData.showFranchiseeEmailOnStatement && franchiseeData.email) {
      detailParts.push(franchiseeData.email);
    }
    const detailLine = detailParts.length > 0 ? detailParts.join('  |  ') : '';
    
    // Build amount section based on balance
    let amountSection = '';
    if (totalBalance > 0) {
      amountSection = `
        <div style="font-size: 18px; font-weight: bold; color: #e74c3c; margin: 20px 0; padding: 15px; background-color: #ffebee; border-radius: 8px; text-align: center;">
          Outstanding amount: R${totalBalance.toFixed(2)}
        </div>
      `;
    } else if (totalBalance < 0) {
      amountSection = `
        <div style="background-color: #e8f5e9; border: 2px solid #4caf50; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0;">
          <p style="margin: 0; color: #2e7d32; font-weight: bold; font-size: 16px;">Your account is in credit with R${Math.abs(totalBalance).toFixed(2)}</p>
        </div>
      `;
    } else {
      amountSection = `
        <div style="background-color: #e8f5e9; border: 2px solid #4caf50; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0;">
          <p style="margin: 0; color: #2e7d32; font-weight: bold; font-size: 16px;">Your account is up to date</p>
        </div>
      `;
    }

    // Custom message section - use franchisee's message or default
    const messageBody = customMessage && customMessage.trim() 
      ? customMessage.trim().replace(/\n/g, '<br>')
      : `Please find attached your ${docTypeLower} for <strong>${student.studentName}</strong> at <strong>${schoolData.schoolName}</strong>.`;

    // Banking details section
    let bankingSection = '';
    if (franchiseeData.includeBankingDetails && franchiseeData.bankName) {
      const reference = student.accountNumber || `${student.studentName} + ${schoolData.schoolName}`;
      bankingSection = `
        <div style="background-color: #f1f1f1; padding: 15px; border-radius: 8px; margin-top: 25px;">
          <p style="font-weight: bold; margin: 0 0 5px 0;">BANKING DETAILS</p>
          <p style="margin: 2px 0;">Bank: ${franchiseeData.bankName}</p>
          <p style="margin: 2px 0;">Account Name: ${franchiseeData.accountName}</p>
          <p style="margin: 2px 0;">Account Number: ${franchiseeData.accountNumber}</p>
          <p style="margin: 2px 0;">Branch code: ${franchiseeData.branchCode}</p>
          <p style="margin: 2px 0; font-weight: bold;">Reference: ${reference}</p>
        </div>
      `;
    }

    return `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background-color: #4A0E4E; padding: 20px; border-radius: 8px 8px 0 0; text-align: center;">
    <h1 style="color: white; margin: 0; font-size: 24px;">${titleBase} ${docType}</h1>
    ${subtitle ? `<p style="color: #E0B0FF; margin: 5px 0 0 0; font-size: 12px; font-style: italic;">${subtitle}</p>` : ''}
    ${detailLine ? `<p style="color: #E0B0FF; margin: 5px 0 0 0; font-size: 12px;">${detailLine}</p>` : ''}
  </div>
  <div style="background-color: #f8f9fa; padding: 25px; border: 1px solid #dee2e6;">
    <p style="color: #666; margin: 0 0 20px 0; font-size: 12px;">Date: ${today}</p>
    <p style="font-size: 15px; color: #333;">Dear Parent/Guardian,</p>
    <p style="font-size: 15px; color: #333; line-height: 1.6;">${messageBody}</p>
    ${amountSection}
    <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <strong>PLEASE NOTE:</strong> All lessons must be paid for prior to the lesson.
    </div>
    ${bankingSection}
    <p style="text-align: center; margin-top: 25px; font-weight: bold; color: #4A0E4E;">Kind regards,<br>The Beaux-Arts Team</p>
  </div>
  <div style="text-align: center; padding: 15px; color: #999; font-size: 11px;">
    <p>A PDF ${docTypeLower} is attached to this email.</p>
  </div>
</div>`;
  }

  /**
   * Legacy: Generate the full invoice HTML (old pattern - fee table in email body)
   * Kept for reference but new emails should use generateCoverLetterHTML
   */
  static generateInvoiceHTML(student, franchiseeData, schoolData, invoiceData) {
    const today = new Date().toLocaleDateString();
    
    // Build term rows
    let termRowsHTML = '';
    
    // Admin fee
    const adminBalance = (invoiceData.adminDue || 0) - (invoiceData.adminPaid || 0);
    termRowsHTML += `
      <tr style="background-color: #fff8e1;">
        <td style="padding: 12px; font-weight: bold;">Admin Fee</td>
        <td style="padding: 12px; text-align: right;">R${(invoiceData.adminDue || 0).toFixed(2)}</td>
        <td style="padding: 12px; text-align: right; color: ${invoiceData.adminPaid > 0 ? '#27ae60' : '#666'};">
          R${(invoiceData.adminPaid || 0).toFixed(2)}
        </td>
        <td style="padding: 12px; text-align: right; font-weight: bold; color: ${adminBalance > 0 ? '#e74c3c' : '#27ae60'};">
          R${adminBalance.toFixed(2)}
        </td>
      </tr>
    `;
    
    // Terms
    if (invoiceData.terms && Array.isArray(invoiceData.terms)) {
      invoiceData.terms.forEach(term => {
        const termBalance = term.due - term.paid;
        const backgroundColor = term.isNextTerm ? '#fffacd' : (term.attended ? '#ffffff' : '#f2f3f5');
        const textColor = term.attended ? '#000' : '#969696';
        
        termRowsHTML += `
          <tr style="background-color: ${backgroundColor}; color: ${textColor};">
            <td style="padding: 12px; font-weight: bold;">${term.label || `Term ${term.termNumber}`}</td>
            <td style="padding: 12px; text-align: right;">R${term.due.toFixed(2)}</td>
            <td style="padding: 12px; text-align: right; color: ${term.paid > 0 ? '#27ae60' : '#666'};">
              R${term.paid.toFixed(2)}
            </td>
            <td style="padding: 12px; text-align: right; font-weight: bold; color: ${termBalance > 0 ? '#e74c3c' : '#27ae60'};">
              R${termBalance.toFixed(2)}
            </td>
          </tr>
        `;
      });
    }
    
    // Total
    const totalBalance = invoiceData.totalBalance || 0;
    termRowsHTML += `
      <tr style="background-color: #f8f9fa; font-weight: bold;">
        <td style="padding: 12px; font-size: 14px;">TOTAL</td>
        <td style="padding: 12px; text-align: right; font-size: 14px;">R${(invoiceData.totalDue || 0).toFixed(2)}</td>
        <td style="padding: 12px; text-align: right; font-size: 14px; color: #27ae60;">
          R${(invoiceData.totalPaid || 0).toFixed(2)}
        </td>
        <td style="padding: 12px; text-align: right; font-size: 14px; color: ${totalBalance > 0 ? '#e74c3c' : '#27ae60'};">
          R${totalBalance.toFixed(2)}
        </td>
      </tr>
    `;
    
    // Banking details
    let bankingDetailsHTML = '';
    if (franchiseeData.includeBankingDetails && franchiseeData.bankName) {
      const reference = student.accountNumber || `${student.studentName}+${schoolData.schoolName}`;
      
      bankingDetailsHTML = `
        <div style="margin-top: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
          <h3 style="color: #4A0E4E; margin-top: 0;">Banking Details</h3>
          <p style="margin: 5px 0;"><strong>Bank:</strong> ${franchiseeData.bankName}</p>
          <p style="margin: 5px 0;"><strong>Account Name:</strong> ${franchiseeData.accountName}</p>
          <p style="margin: 5px 0;"><strong>Account Number:</strong> ${franchiseeData.accountNumber}</p>
          <p style="margin: 5px 0;"><strong>Branch Code:</strong> ${franchiseeData.branchCode}</p>
          <p style="margin: 5px 0;"><strong>Reference:</strong> ${reference}</p>
        </div>
      `;
    }
    
    // Complete HTML
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Beaux-Arts Invoice</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #4A0E4E; padding-bottom: 20px;">
          <h1 style="color: #4A0E4E; margin: 0;">BEAUX-ARTS ${totalBalance > 0 ? 'INVOICE' : 'STATEMENT'}</h1>
          <p style="color: #666; margin: 5px 0;">Contact: ${franchiseeData.whatsapp || franchiseeData.phone || 'N/A'}</p>
        </div>
        
        <div style="background-color: #f8f9ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #4169E1;">
          <p style="margin: 5px 0;"><strong>Date:</strong> ${today}</p>
          <p style="margin: 5px 0;"><strong>Student:</strong> ${student.studentName}</p>
          <p style="margin: 5px 0;"><strong>School:</strong> ${schoolData.schoolName}</p>
          <p style="margin: 5px 0;"><strong>Account:</strong> ${student.accountNumber || 'N/A'}</p>
        </div>
        
        <h2 style="color: #4A0E4E;">Payment Breakdown:</h2>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <thead>
            <tr style="background-color: #4A0E4E; color: white;">
              <th style="padding: 12px; text-align: left;">Description</th>
              <th style="padding: 12px; text-align: right;">DUE</th>
              <th style="padding: 12px; text-align: right;">PAID</th>
              <th style="padding: 12px; text-align: right;">BALANCE</th>
            </tr>
          </thead>
          <tbody>
            ${termRowsHTML}
          </tbody>
        </table>
        
        <div style="font-size: 24px; font-weight: bold; color: #e74c3c; margin: 20px 0; padding: 15px; background-color: #ffebee; border-radius: 8px; text-align: center;">
          Amount Due: R${totalBalance.toFixed(2)}
        </div>
        
        ${totalBalance === 0 ? `
          <div style="background-color: #e8f5e9; border: 2px solid #4caf50; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0;">
            <p style="margin: 0; color: #2e7d32; font-weight: bold; font-size: 16px;">Your account is up to date</p>
          </div>
        ` : ''}
        
        <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <strong>IMPORTANT:</strong> All lessons must be paid for prior to the lesson.
        </div>
        
        ${bankingDetailsHTML}
        
        <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #ddd; text-align: center; color: #666; font-size: 14px;">
          <p>Thank you, the Beaux-Arts team</p>
          <p style="font-size: 12px;">This is an automated email.</p>
        </div>
      </body>
      </html>
    `;
  }
}

export default EmailService;